<script setup lang="ts">
import { Head, useForm } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import banker from '@/routes/banker'
import bankerPassword from '@/routes/banker/password'
import InertiaFormField from '@/components/InertiaFormField.vue'

const { t } = useI18n()

const form = useForm({
  email: '',
  password: '',
  remember: false,
})

const onFormSubmit = () => {
  form.post(banker.login().url, {
    preserveScroll: true,
    onError: () => {
      form.reset('password')
    },
  })
}
</script>

<template>
  <Head :title="t('title')" />

  <AuthCard>
    <template #title>{{ t('title') }}</template>
    <template #subtitle>
      {{ t('subtitle') }}
    </template>
    <template #content>
      <form class="flex flex-col gap-4" @submit.prevent="onFormSubmit">
        <div class="flex flex-col gap-4">
          <InertiaFormField :error="form.errors.email">
            <IconField>
              <InputIcon class="pi pi-envelope !z-10" />
              <InputText
                v-model="form.email"
                name="email"
                type="email"
                :placeholder="t('form.email.placeholder')"
                required
                fluid />
            </IconField>
          </InertiaFormField>
          <InertiaFormField :error="form.errors.password">
            <IconField>
              <InputIcon class="pi pi-lock !z-10" />
              <Password
                v-model="form.password"
                name="password"
                :placeholder="t('form.password.placeholder')"
                :feedback="false"
                toggle-mask
                required
                fluid />
            </IconField>
          </InertiaFormField>

          <div class="mt-8 flex justify-between gap-2">
            <label for="remember_me" class="flex cursor-pointer items-center gap-2">
              <Checkbox v-model="form.remember" binary name="remember" input-id="remember_me" />
              <span>{{ t('form.remember-me') }}</span>
            </label>
            <Button
              type="submit"
              :label="t('form.submit')"
              :dt="{ padding: { x: '4rem' } }"
              :loading="form.processing" />
          </div>
        </div>
      </form>
    </template>
    <template #footer>
      <Divider />
      <div class="flex justify-start">
        <Link :href="bankerPassword.request()">{{ t('links.forgot-password') }}</Link>
      </div>
    </template>
  </AuthCard>
</template>

<i18n>
{
  "en": {
    "title": "Login",
    "subtitle": "Please enter your credentials to login",
    "form": {
      "email": {
        "placeholder": "Email",
        "required": "Email is required",
        "email": "This is not a valid email address"
      },
      "password": {
        "placeholder": "Password",
        "required": "Password is required"
      },
      "remember-me": "Remember me",
      "submit": "Login"
    },
    "links": {
      "forgot-password": "Forgot Password?"
    }
  },
  "de": {
    "title": "Anmelden",
    "subtitle": "Bitte geben Sie Ihre Anmeldeinformationen ein",
    "form": {
      "email": {
        "placeholder": "E-Mail",
        "required": "E-Mail ist erforderlich",
        "email": "Dies ist keine gültige E-Mail-Adresse"
      },
      "password": {
        "placeholder": "Passwort",
        "required": "Passwort ist erforderlich"
      },
      "remember-me": "Angemeldet bleiben",
      "submit": "Anmelden"
    },
    "links": {
      "forgot-password": "Passwort vergessen?"
    }
  }
}
</i18n>
